import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, effect, inject, input, signal } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { TranslocoService } from '@ngneat/transloco';
import moment, { Moment } from 'moment';
import { dateTimeFormats } from '@shared/constants/dateTimeFormats';
import { SharedModule } from '@shared/shared.module';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { loadHdChartGlobalSettings, saveHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdTransitInfoCardComponent } from '../info-card/hd-transit-info-card.component';
import { HdTransitInfoCardData, nowNextToInfoCardData } from '../info-card/hd-transit-info-card.models';
import { HdTransitLegendFilterComponent } from '../legend-filter/hd-transit-legend-filter.component';
import { HdTransitShiftPopupService } from '../shift-popup/hd-transit-shift-popup.service';
import { formatGateLine, msUntil, selectCountdownFormat } from '../transit-countdown.utils';
import { TransitDataService } from '../transit-data.service';
import { HdTransitLegendCategory, legendCategoryOf } from '../transit-legend.utils';
import { TransitViewTimeService } from '../transit-view-time.service';
import { HdTransitsViewStateService } from '../transits-view-state.service';
import { HdTransitNowNextPlanet } from '../transit.models';
import {
  HdTransitNowNextSortMode,
  sortNowNextPlanets,
} from './hd-transit-now-next-panel.utils';

const I18N_PREFIX = 'humanDesign.chart.transits.panel';

/** Weergavemodus van het panel: kaarten (nieuwe info-cards) of de klassieke lijst. */
export type HdTransitNowNextViewMode = 'cards' | 'list';

/** Alle 14 planeten — Chiron zit altijd in de data; de UI filtert via de showChiron-setting (besluit 9). */
const ALL_PLANET_KEYS: readonly HdPlanetKey[] = HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map(config => config.key);

/** Weergave-model van één planeetrij — bewust tick-onafhankelijk (performance-analyse fix 2). */
interface HdTransitNowNextRowVm {
  readonly planetId: number;
  readonly planetKey: HdPlanetKey;
  /** Legenda-categorie van de rij — stuurt welk legenda-item zijn kleur deelt. */
  readonly legendCategory: HdTransitLegendCategory;
  readonly name: string;
  readonly glyph: string;
  readonly stripeColor: string;
  readonly retrograde: boolean;
  readonly prevLine: string | null;
  readonly current: string;
  readonly nextLine: string | null;
  /** Lijn-keynotes van de vorige/huidige/volgende lijn (hover-tooltip); null als de scope nog leeg is. */
  readonly prevLineKeynote: string | null;
  readonly currentLineKeynote: string | null;
  readonly nextLineKeynote: string | null;
  /** "sinds"-label met volledig tijdstip van het huidige LIJN-segment (tooltip op het ◂-pijltje). */
  readonly lineSinceTooltip: string;
  /** Volledig tijdstip van de eerstvolgende lijn-wissel (tooltip op het ▸-pijltje). */
  readonly lineShiftAtTooltip: string | null;
  readonly prevGate: number | null;
  /** Huidige poort-nummer — altijd bekend via `current.gate`. */
  readonly currentGate: number;
  readonly nextGate: number | null;
  /** Poort-keynotes (keynote 1 · keynote 2) van de vorige/huidige/volgende poort. */
  readonly prevGateKeynote: string | null;
  readonly currentGateKeynote: string | null;
  readonly nextGateKeynote: string | null;
  /** "sinds"-label met volledig tijdstip van de huidige POORT; null als de API die niet leverde. */
  readonly gateSinceTooltip: string | null;
  /** Volledig tijdstip van de eerstvolgende poort-wissel (tooltip op het ▸-pijltje). */
  readonly gateShiftAtTooltip: string | null;
  /** Invoer van de transit-info-card in de card view. */
  readonly cardData: HdTransitInfoCardData;
  /** Bron-entry — bouwt bij een klik de popup-data. */
  readonly source: HdTransitNowNextPlanet;
}

/** Live countdown-teksten van één rij — het enige tick-afhankelijke stuk. */
interface HdTransitNowNextCountdownVm {
  readonly line: string | null;
  readonly gate: string | null;
}

/**
 * UTC-Moment → volledige lokale datum + tijd van de kijker (hover-tooltips).
 * De dagnaam volgt de huidige app-locale: een geladen Moment houdt anders de
 * locale van zijn aanmaak (ook na `clone`).
 */
function formatLocalDateTime(time: Moment): string {
  return time.clone().local().locale(moment.locale()).format(dateTimeFormats.dayAsWordWithTime);
}

/** Elke poort die in een rij voorkomt (vorige/huidige/volgende, lijn- én poort-niveau). */
function collectReferencedGates(planets: readonly HdTransitNowNextPlanet[]): Set<number> {
  const gates = new Set<number>();
  for (const planet of planets) {
    gates.add(planet.current.gate);
    if (planet.currentGate != null) gates.add(planet.currentGate.gate);
    if (planet.prevLine != null) gates.add(planet.prevLine.gate);
    if (planet.nextLine != null) gates.add(planet.nextLine.gate);
    if (planet.prevGate != null) gates.add(planet.prevGate.gate);
    if (planet.nextGate != null) gates.add(planet.nextGate.gate);
  }
  return gates;
}

/** Sleutels in de keynote-tooltipmap: `g26` voor een poort, `g26.3` voor een lijn. */
function gateTooltipKey(gate: number): string {
  return `g${gate}`;
}

function lineTooltipKey(gate: number, line: number): string {
  return `g${gate}.${line}`;
}

/**
 * Nu & Straks-panel (spec §4, visualisatie-doc §2): per planeet vorige ◂
 * huidige ▸ volgende op lijn-niveau (regel 1) én op poort-niveau (regel 2), en
 * een live aftellende countdown per regel. De wisseltijden zitten als hover-
 * tooltip op de pijltjes (◂ = sinds wanneer de huidige stand loopt, ▸ = wanneer
 * de volgende ingaat) en de nummers tonen hun keynote(s) bij hover. Klik op een
 * rij opent de gedeelde shift-popup met de now-next context van die planeet.
 *
 * - Data: één live `getNowNext` bij init; daarna tikt het panel client-side
 *   mee via `TransitViewTimeService.viewTime` en refetcht de service enkel
 *   wanneer een geladen shift gepasseerd is (`refreshNowNextIfNeeded`,
 *   besluit 6).
 * - Chiron: geen eigen toggle — de globale `showChiron`-chartsetting is de
 *   default van de input; een parent mag overrulen (besluit 9).
 * - Werkt zonder geladen chart (globale modus, besluit 14).
 * - Layout: de rij-lijst schakelt via container queries naar 1/2/3 kolommen
 *   (~340px per rij) — in de smalle dock blijft dat automatisch 1 kolom.
 */
@Component({
  selector: 'hd-transit-now-next-panel',
  imports: [SharedModule, ESegmentedToggleComponent, HdTransitInfoCardComponent, HdTransitLegendFilterComponent, ESplashLoaderComponent],
  templateUrl: './hd-transit-now-next-panel.component.html',
  styleUrl: './hd-transit-now-next-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitNowNextPanelComponent implements OnInit {
  readonly showChiron = input<boolean>(loadHdChartGlobalSettings().showChiron);

  private readonly dataService = inject(TransitDataService);
  private readonly viewTime = inject(TransitViewTimeService);
  private readonly popupService = inject(HdTransitShiftPopupService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly viewState = inject(HdTransitsViewStateService);

  protected readonly loading: Signal<boolean> = this.dataService.nowNextLoading;
  protected readonly error: Signal<string | null> = this.dataService.nowNextError;
  /** Sorteermodus + weergavemodus: gebruikerssettings (gepersisteerd bij wijziging). */
  protected readonly sortMode = signal<HdTransitNowNextSortMode>(loadHdChartGlobalSettings().transitsNowNextSortMode);
  protected readonly viewMode = signal<HdTransitNowNextViewMode>(loadHdChartGlobalSettings().transitsNowNextViewMode);

  protected readonly sortModes: readonly HdTransitNowNextSortMode[] = ['planetOrder', 'nextShift'];
  protected readonly sortModeLabelKeys: Readonly<Record<HdTransitNowNextSortMode, string>> = {
    planetOrder: 'sortByPlanet',
    nextShift: 'sortByNextShift',
  };
  /** Font Awesome-iconen voor de sorteer-toggle (labels blijven als aria/tooltip). */
  private readonly sortModeIcons: Readonly<Record<HdTransitNowNextSortMode, string>> = {
    planetOrder: 'solar-system',
    nextShift: 'clock',
  };
  protected readonly viewModes: readonly HdTransitNowNextViewMode[] = ['cards', 'list'];
  protected readonly viewModeLabelKeys: Readonly<Record<HdTransitNowNextViewMode, string>> = {
    cards: 'viewCards',
    list: 'viewList',
  };
  /** Font Awesome-iconen voor de view-toggle (labels blijven als aria/tooltip). */
  private readonly viewModeIcons: Readonly<Record<HdTransitNowNextViewMode, string>> = {
    cards: 'grip',
    list: 'list',
  };

  /** Icoonknoppen van de sorteer-toggle (gedeelde e-segmented-toggle). */
  protected readonly sortOptions = computed<readonly ESegmentedToggleOption<HdTransitNowNextSortMode>[]>(() =>
    this.sortModes.map(mode => ({ value: mode, label: this.t(this.sortModeLabelKeys[mode]), icon: this.sortModeIcons[mode] })),
  );

  /** Icoonknoppen van de view-toggle (Kaarten/Lijst). */
  protected readonly viewOptions = computed<readonly ESegmentedToggleOption<HdTransitNowNextViewMode>[]>(() =>
    this.viewModes.map(mode => ({ value: mode, label: this.t(this.viewModeLabelKeys[mode]), icon: this.viewModeIcons[mode] })),
  );

  private readonly planets = signal<readonly HdTransitNowNextPlanet[]>([]);

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Keynote-tooltips per zichtbaar poort- en lijnnummer. Bewust een aparte
   * computed: de vertalingen hangen enkel van de data en de taal af, zodat de
   * seconde-tick van `rows` ze niet telkens opnieuw hoeft op te lossen.
   */
  private readonly keynoteTooltips = computed<ReadonlyMap<string, string>>(() => {
    const lang = this.lang();
    const tooltips = new Map<string, string>();
    for (const gate of collectReferencedGates(this.planets())) {
      const translations = this.gateInfo.gateInfoIn(gate, lang).translations;
      const keynotes = [translations.keynote1, translations.keynote2].filter(keynote => keynote.length > 0);
      if (keynotes.length > 0) tooltips.set(gateTooltipKey(gate), keynotes.join(' · '));
      translations.lines.forEach((line, index) => {
        if (line.keynote.length > 0) tooltips.set(lineTooltipKey(gate, index + 1), line.keynote);
      });
    }
    return tooltips;
  });

  /**
   * Card-data per planeet — bewust buiten de tick: zo behouden de kaart-inputs
   * hun object-identiteit en worden de OnPush-kaarten niet elke seconde dirty
   * (dat was de bron van het hakkelige scrollen; zie
   * docs/human-design/transits-performance-analyse.md).
   */
  private readonly cardDataByPlanetId = computed<ReadonlyMap<number, HdTransitInfoCardData>>(() => {
    const map = new Map<number, HdTransitInfoCardData>();
    for (const planet of this.planets()) {
      const cardData = nowNextToInfoCardData(planet);
      if (cardData != null) map.set(planet.planetId, cardData);
    }
    return map;
  });

  /**
   * Rijen herberekenen per data/taal/sorteer/filter-wijziging — bewust NIET per
   * klok-tick; de live countdowns leven apart in {@link countdowns}.
   */
  protected readonly rows = computed<readonly HdTransitNowNextRowVm[]>(() => {
    const showChiron = this.showChiron();
    const tooltips = this.keynoteTooltips();
    const enabledCategories = this.viewState.enabledLegendCategories();
    const cardDataByPlanetId = this.cardDataByPlanetId();
    const rows: HdTransitNowNextRowVm[] = [];
    for (const planet of sortNowNextPlanets(this.planets(), this.sortMode())) {
      const key = planet.planetKey;
      if (key == null || (key === 'chiron' && !showChiron)) continue;
      const config = getHdPlanetDisplayConfig(key);
      // De filter-legende vinkt categorieën aan/uit — gedeeld met de lanes.
      if (!enabledCategories.has(legendCategoryOf(key, config.type))) continue;
      const cardData = cardDataByPlanetId.get(planet.planetId);
      if (cardData == null) continue;
      rows.push(this.buildRow(planet, key, tooltips, cardData));
    }
    return rows;
  });

  /** Live countdowns per planeet — de enige berekening die per klok-tick loopt. */
  protected readonly countdowns = computed<ReadonlyMap<number, HdTransitNowNextCountdownVm>>(() => {
    const now = this.viewTime.viewTime();
    const map = new Map<number, HdTransitNowNextCountdownVm>();
    for (const planet of this.planets()) {
      map.set(planet.planetId, {
        line: planet.nextLine != null ? this.countdownText(planet.nextLine.atUtc, now) : null,
        gate: planet.nextGate != null ? this.countdownText(planet.nextGate.atUtc, now) : null,
      });
    }
    return map;
  });

  /**
   * Filter-legende van de rij-accentkleuren — de gedeelde
   * `hd-transit-legend-filter` component toont de items; de filter-staat leeft
   * in de view-state service en werkt op de rijen/kaarten én de lanes.
   */

  constructor() {
    // De tick hertekent via `rows`; dit effect herbevraagt alleen wanneer de
    // service zijn geldig-tot gepasseerd ziet (no-op zolang de cache vers is).
    effect(() => {
      const now = this.viewTime.viewTime();
      void this.dataService.refreshNowNextIfNeeded(now).then(refreshed => {
        if (refreshed != null) this.planets.set(refreshed);
      });
    });
  }

  ngOnInit(): void {
    void this.dataService.getNowNext(null, ALL_PLANET_KEYS).then(planets => this.planets.set(planets));
  }

  protected setSortMode(mode: HdTransitNowNextSortMode): void {
    this.sortMode.set(mode);
    this.persistViewSettings();
  }

  protected setViewMode(mode: HdTransitNowNextViewMode): void {
    this.viewMode.set(mode);
    this.persistViewSettings();
  }

  /** View-voorkeuren (cards/list + sortering) bijhouden in de gebruikerssettings. */
  private persistViewSettings(): void {
    saveHdChartGlobalSettings({
      ...loadHdChartGlobalSettings(),
      transitsNowNextViewMode: this.viewMode(),
      transitsNowNextSortMode: this.sortMode(),
    });
  }

  /** Klik op een rij of kaart → shift-popup met de now-next context van die planeet. */
  protected openShiftPopup(row: HdTransitNowNextRowVm, event?: MouseEvent): void {
    const planet = row.source;
    this.popupService.open(
      {
        planetId: planet.planetId,
        planetKey: planet.planetKey,
        gate: planet.current.gate,
        line: planet.current.line,
        fromUtc: planet.current.sinceUtc,
        toUtc: planet.nextLine?.atUtc ?? null,
        retrograde: planet.retrograde,
        context: planet,
      },
      event != null ? { x: event.clientX, y: event.clientY } : undefined,
    );
  }

  /** Enter/Space op een gefocuste kaart opent ook de popup (zonder cursor-anker). */
  protected onCardKeydown(row: HdTransitNowNextRowVm, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openShiftPopup(row);
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`${I18N_PREFIX}.${key}`, params, this.lang());
  }

  private buildRow(
    planet: HdTransitNowNextPlanet,
    key: HdPlanetKey,
    tooltips: ReadonlyMap<string, string>,
    cardData: HdTransitInfoCardData,
  ): HdTransitNowNextRowVm {
    const config = getHdPlanetDisplayConfig(key);
    const lineKeynote = (transition: { gate: number; line: number } | null): string | null =>
      transition != null ? tooltips.get(lineTooltipKey(transition.gate, transition.line)) ?? null : null;
    const gateKeynote = (gate: number | null): string | null =>
      gate != null ? tooltips.get(gateTooltipKey(gate)) ?? null : null;
    return {
      planetId: planet.planetId,
      planetKey: key,
      legendCategory: legendCategoryOf(key, config.type),
      name: this.planetInfo.planetInfoIn(key, this.lang()).name,
      glyph: config.astronomiconChar,
      stripeColor: config.stripeColor,
      retrograde: planet.retrograde,
      prevLine: planet.prevLine != null ? formatGateLine(planet.prevLine.gate, planet.prevLine.line) : null,
      current: formatGateLine(planet.current.gate, planet.current.line),
      nextLine: planet.nextLine != null ? formatGateLine(planet.nextLine.gate, planet.nextLine.line) : null,
      prevLineKeynote: lineKeynote(planet.prevLine),
      currentLineKeynote: lineKeynote(planet.current),
      nextLineKeynote: lineKeynote(planet.nextLine),
      lineSinceTooltip: this.t('sinceLabel', { time: formatLocalDateTime(planet.current.sinceUtc) }),
      lineShiftAtTooltip:
        planet.nextLine != null
          ? this.t('shiftAtLabel', { time: formatLocalDateTime(planet.nextLine.atUtc) })
          : null,
      prevGate: planet.prevGate?.gate ?? null,
      currentGate: planet.current.gate,
      nextGate: planet.nextGate?.gate ?? null,
      prevGateKeynote: gateKeynote(planet.prevGate?.gate ?? null),
      currentGateKeynote: gateKeynote(planet.current.gate),
      nextGateKeynote: gateKeynote(planet.nextGate?.gate ?? null),
      gateSinceTooltip:
        planet.currentGate != null
          ? this.t('sinceLabel', { time: formatLocalDateTime(planet.currentGate.sinceUtc) })
          : null,
      gateShiftAtTooltip:
        planet.nextGate != null
          ? this.t('shiftAtLabel', { time: formatLocalDateTime(planet.nextGate.atUtc) })
          : null,
      cardData,
      source: planet,
    };
  }

  private countdownText(target: Moment, now: Moment): string {
    const format = selectCountdownFormat(msUntil(target, now));
    return this.t(format.key, format.params);
  }
}
